// Reference Library rules. Words: REFERENCE_LIBRARY_WORDS.md. Mockup: MOCKUP_REFERENCE_LIBRARY_APPROVED.html (approved 2026-10-08).
// Data:
//   references/{refId}                        the record
//   settings/referenceCopy                    when the copy job last ran (written by the job)
//   Storage companies/{c}/references/incoming/{name}   where the page uploads
//   Storage companies/{c}/references/{refId}/{name}    where saveReference moves it
// Pure: no database. Shared by the page, the server steps (functions/src/references.ts) and the tests.
//
// Rules:
// * Admins only. A QEW never sees the library.
// * Nothing is deleted. A new edition supersedes the old one; Retire supersedes with a reason.
// * One record per number and edition. Kind has no default. A standard needs an edition.
// * Public sources (OSHA on eCFR) are read live, not uploaded.

import { dateInSiteTime } from "./training-server";

export const KINDS = ["Standard", "Program Document", "Manufacturer", "Site Document", "Other"] as const;
export type Kind = (typeof KINDS)[number];

/** Short labels for the kind buttons on a phone. */
export const KIND_SHORT: Record<Kind, string> = {
  Standard: "Standard",
  "Program Document": "Program",
  Manufacturer: "Manufacturer",
  "Site Document": "Site",
  Other: "Other",
};

/** Plural headings for the list groups. */
export const KIND_GROUP: Record<Kind, string> = {
  Standard: "Standards",
  "Program Document": "Program Documents",
  Manufacturer: "Manufacturer",
  "Site Document": "Site Documents",
  Other: "Other",
};

export const MAX_BYTES = 50 * 1024 * 1024;

/** Content types the Storage rule accepts. Same list as storage.rules. */
export const FILE_TYPES: Record<string, string> = {
  "application/pdf": "PDF",
  "application/msword": "Word",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word",
  "text/plain": "Text",
  "text/markdown": "Text",
};

/** What the file picker offers. */
export const ACCEPT = ".pdf,.doc,.docx,.txt,.md,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain,text/markdown";

export const LIVE_SOURCES_LINE = "OSHA 29 CFR 1910 is read live from eCFR. No upload needed.";

/** Where the copy job runs on demand. */
export const COPY_JOB_URL = "https://github.com/ActivSolv4/getsafetag/actions/workflows/reference-copy.yml";

export type Status = "Current" | "Superseded";

export interface HistoryEntry {
  action: string;
  at: string;
  by: string;
  byName: string;
  reason?: string;
  fileName?: string;
  storagePath?: string;
}

export interface Reference {
  refId: string;
  kind: Kind;
  title: string;
  number: string;
  edition: string;
  covers: string;
  notes: string;
  status: Status;
  replacedBy: string;
  replaces: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  storagePath: string;
  /** Set by the copy job: true when text came out of the file. Null until the first copy. */
  hasText: boolean | null;
  addedOn: string;
  addedByName: string;
  history: HistoryEntry[];
}

export interface CopyLine {
  lastCopiedAt: string;
  lastResult: string;
  lastError: string;
}

// ---- Words on the page ----

export function isKind(v: unknown): v is Kind {
  return typeof v === "string" && (KINDS as readonly string[]).includes(v);
}

/** 1572864 -> "1.5 MB". */
export function sizeText(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function fileTypeText(contentType: string): string {
  return FILE_TYPES[contentType] ?? "File";
}

/** "NFPA 70E 2024", or the title when there is no number. */
export function shortName(r: Pick<Reference, "number" | "edition" | "title">): string {
  const base = r.number.trim() || r.title.trim();
  return r.edition.trim() ? `${base} ${r.edition.trim()}` : base;
}

/** 2026-10-08T06:00:00Z -> "10/08 at 2:00 AM" (site time). */
export function copyTimeText(iso: string): string {
  const d = new Date(iso);
  if (!iso || Number.isNaN(d.getTime())) return "";
  const day = dateInSiteTime(d);
  const time = new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }).format(d);
  return `${day.slice(5, 7)}/${day.slice(8, 10)} at ${time}`;
}

/** The line on top of the list. */
export function copyLineOf(c: CopyLine | null, newestAddedAt: string): { tone: "green" | "amber" | "red"; text: string } {
  if (!c || !c.lastCopiedAt) return { tone: "amber", text: "Not copied for Claude yet. Press Copy Now when the library is ready." };
  if (c.lastResult && c.lastResult !== "ok") return { tone: "red", text: `The last copy for Claude failed ${copyTimeText(c.lastCopiedAt)}. ${c.lastError || "Open the copy job for the reason."}` };
  if (newestAddedAt && newestAddedAt > c.lastCopiedAt) return { tone: "amber", text: `Copied for Claude ${copyTimeText(c.lastCopiedAt)}. Newer uploads go over on the next Copy Now.` };
  return { tone: "green", text: `Copied for Claude ${copyTimeText(c.lastCopiedAt)}.` };
}

// ---- The list ----

export interface KindGroup {
  kind: Kind;
  current: number;
  superseded: number;
  refs: Reference[];
}

function idNumber(refId: string): number {
  const m = /^REF-(\d+)$/.exec(refId);
  return m?.[1] ? Number(m[1]) : Number.MAX_SAFE_INTEGER;
}

export function refMatches(r: Reference, search: string): boolean {
  const q = search.trim().toLowerCase();
  if (!q) return true;
  return [r.title, r.number, r.edition, r.refId, r.covers].some((x) => x.toLowerCase().includes(q));
}

/** Groups in kind order. Current first, then superseded; newest edition first inside each. Counts are the whole kind. */
export function groupRefs(refs: Reference[], search = ""): KindGroup[] {
  return KINDS.map((kind) => {
    const all = refs.filter((r) => r.kind === kind);
    const shown = all
      .filter((r) => refMatches(r, search))
      .sort(
        (a, b) =>
          Number(a.status === "Superseded") - Number(b.status === "Superseded") ||
          a.number.localeCompare(b.number) ||
          b.edition.localeCompare(a.edition, "en", { numeric: true }) ||
          idNumber(a.refId) - idNumber(b.refId),
      );
    return { kind, current: all.filter((r) => r.status === "Current").length, superseded: all.filter((r) => r.status === "Superseded").length, refs: shown };
  }).filter((g) => g.refs.length || g.current || g.superseded);
}

// ---- IDs ----

/** One past every ID on file. REF-0001 first. Never reused. */
export function nextRefId(existing: string[]): string {
  let max = 0;
  for (const id of existing) {
    const n = idNumber(id);
    if (n !== Number.MAX_SAFE_INTEGER && n > max) max = n;
  }
  return `REF-${String(max + 1).padStart(4, "0")}`;
}

// ---- Checks ----

export interface RefInput {
  kind: string;
  title: string;
  number: string;
  edition: string;
  covers: string;
  notes: string;
}

export interface FileInfo {
  name: string;
  size: number;
  type: string;
}

const norm = (s: string) => s.trim().toLowerCase().replace(/\s+/g, " ");

/** The record already holding this number and edition, if any (any status). */
export function sameEdition(all: Reference[], number: string, edition: string, exceptId = ""): Reference | null {
  if (!number.trim() || !edition.trim()) return null;
  return all.find((r) => r.refId !== exceptId && norm(r.number) === norm(number) && norm(r.edition) === norm(edition)) ?? null;
}

export function fileProblem(f: FileInfo | null): string | null {
  if (!f) return "Choose the file.";
  if (!FILE_TYPES[f.type]) return "Pick a PDF, Word or text file.";
  if (f.size > MAX_BYTES) return `That file is ${sizeText(f.size)}. The limit is 50 MB.`;
  if (f.size <= 0) return "That file is empty.";
  return null;
}

/** The fields, without the file. Kind has no default. A standard needs an edition. */
export function fieldsProblem(input: RefInput): string | null {
  if (!isKind(input.kind)) return "Pick the kind.";
  if (!input.title.trim()) return "Enter the title.";
  if (input.title.trim().length > 140) return "Keep the title under 140 characters.";
  if (input.kind === "Standard" && !input.edition.trim()) return "Enter the edition, like 2024.";
  if (input.number.trim().length > 60) return "Keep the number under 60 characters.";
  if (input.edition.trim().length > 30) return "Keep the edition under 30 characters.";
  if (input.covers.trim().length > 120) return "Keep Covers under 120 characters.";
  if (input.notes.trim().length > 500) return "Keep the notes under 500 characters.";
  return null;
}

/** A new reference. The same number and edition is refused. */
export function newRefProblem(input: RefInput, file: FileInfo | null, all: Reference[]): string | null {
  const f = fileProblem(file);
  if (f) return f;
  const p = fieldsProblem(input);
  if (p) return p;
  const dupe = sameEdition(all, input.number, input.edition);
  if (dupe) return `${shortName(dupe)} is already ${dupe.refId}. Open it to replace its file.`;
  return null;
}

/** A new edition of an existing reference: same kind and number, a different edition. */
export function newEditionProblem(input: RefInput, file: FileInfo | null, old: Reference, all: Reference[]): string | null {
  if (old.status !== "Current") return `${old.refId} is already superseded. Add the new edition to the current one.`;
  const base = newRefProblem(input, file, all);
  if (base) return base;
  if (input.kind !== old.kind) return `A new edition stays a ${old.kind}.`;
  if (norm(input.number) !== norm(old.number)) return `A new edition keeps the number ${old.number || "blank"}.`;
  if (!input.edition.trim()) return "Enter the new edition.";
  return null;
}

/** Save stays off until the file, kind and title are in, and the edition for a standard. */
export function canSave(input: RefInput, file: FileInfo | null): boolean {
  return !fileProblem(file) && isKind(input.kind) && Boolean(input.title.trim()) && (input.kind !== "Standard" || Boolean(input.edition.trim()));
}

/** Save on each form: Replace File needs only the file; Add and New Edition need the fields too. */
export function formReady(mode: "new" | "edition" | "replace", input: RefInput, file: FileInfo | null): boolean {
  return mode === "replace" ? !fileProblem(file) : canSave(input, file);
}

export function reasonProblem(reason: string): string | null {
  const r = reason.trim();
  if (!r) return "Type the reason.";
  if (r.length > 200) return "Keep the reason under 200 characters.";
  return null;
}

/** A file name safe for a storage path: letters, digits, dot, dash, underscore. */
export function safeFileName(name: string): string {
  const cleaned = name.normalize("NFKD").replace(/[^\w.\-]+/g, "_").replace(/_+/g, "_").replace(/^[._]+/, "");
  return (cleaned || "file").slice(0, 120);
}

export function incomingPath(company: string, uploadName: string): string {
  return `companies/${company}/references/incoming/${uploadName}`;
}

export function finalPath(company: string, refId: string, fileName: string): string {
  return `companies/${company}/references/${refId}/${safeFileName(fileName)}`;
}

/** What a history entry reads as. */
export function historyText(h: HistoryEntry): string {
  const what: Record<string, string> = {
    added: "Added",
    changed: "Changed",
    "file replaced": "File replaced",
    superseded: "Superseded",
    retired: "Retired",
  };
  return what[h.action] ?? h.action;
}
